import { computed } from "vue";
import { useI18n } from "vue-i18n";
import {
    arrayField,
    buttonField,
    createBlock,
    descriptiveImageField,
    headingField,
    htmlField,
    imageField,
    textField,
} from "./blockBuilder";
import type { BlockDef } from "./block.interfaces";
import { useDefaultColors } from "./useDefaultColors";
import { AVATAR_API_BASE_URL, getPaceholderImageUrl } from "@/apps/page/services/page.service";
import { useGetters } from "@/store/vuex-composition-mappers";
import {
    Centered2x2Grid,
    WithWideImages,
    WithTieredImages,
    WithTestimonial,
    WithSquareImages,
    WithProductScreenShot,
    WithLargeScreenshot,
    WithHeaderImagesAndDescriptions,
    WithAlternatingSections,
    SimpleThreeColumnWithSmallIcons,
    SimpleThreeColumnWithLargeIcons,
    Simple,
    OffsetWithFeatureList,
    Offset2x2Grid,
    ContainedInPanel,
    WithProductScreenshotOnLeft,
    WithSplitImage,
} from "@/views/blocks/features/features";
import type {
    Centered2x2GridProps,
    WithWideImagesProps,
    WithTieredImagesProps,
    WithTestimonialProps,
    WithSquareImagesProps,
    WithProductScreenshotProps,
    WithProductScreenshotOnLeftProps,
    WithSplitImageProps,
    WithLargeScreenshotProps,
    WithHeaderImagesAndDescriptionsProps,
    WithAlternatingSectionsProps,
    SimpleThreeColumnWithSmallIconsProps,
    SimpleThreeColumnWithLargeIconsProps,
    SimpleProps,
    OffsetWithFeatureListProps,
    Offset2x2GridProps,
    ContainedInPanelProps,
} from "@/views/blocks/features/features";
import imageSmSrc from "@/views/blocks/shared/images/image-sm.svg";
import imageMdSrc from "@/views/blocks/shared/images/image-md.svg";
import { useFieldConfig } from "./useFieldConfig";

export function useFeatures() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    // @ts-expect-error TODO: Fix typing in the future
    const { pageColors } = useGetters("page", ["pageColors"]);
    const { translations } = useFieldConfig();

    const centered2x2GridBlock = computed<BlockDef>(() =>
        createBlock<Centered2x2GridProps>({
            typeId: "Centered2x2Grid",
            type: "features",
            name: t("page.blocks.features.centered2x2Grid"),
            component: Centered2x2Grid,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Features", translations.value.text)
            )
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Everything you need to deploy your app",
                    "h2",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Our platform provides all the essential tools and features you need to deploy, secure, and scale your application with minimal effort.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        iconSrc: {
                            label: translations.value.iconSrc,
                            type: "image",
                        },
                    },
                    [
                        {
                            title: "Push to Deploy",
                            description:
                                "Deploy your application with a single click. Our streamlined deployment process eliminates configuration headaches and gets your app running in seconds.",
                            iconSrc: imageMdSrc,
                        },
                        {
                            title: "SSL Certificates",
                            description:
                                "Automatic SSL certificate generation and renewal keeps your application secure. We handle all the complexity of HTTPS so you can focus on building your product.",
                            iconSrc: imageMdSrc,
                        },
                        {
                            title: "Simple Queues",
                            description:
                                "Manage background tasks efficiently with our easy-to-use queue system. Perfect for handling email sending, data processing, and other asynchronous operations.",
                            iconSrc: imageMdSrc,
                        },
                        {
                            title: "Advanced Security",
                            description:
                                "Enterprise-grade security features protect your data and users. Includes DDoS protection, regular security audits, and compliance with industry standards.",
                            iconSrc: imageMdSrc,
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                        iconSrc: imageMdSrc,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const withProductScreenshotBlock = computed<BlockDef>(() =>
        createBlock<WithProductScreenshotProps>({
            typeId: "WithProductScreenShot",
            type: "features",
            name: t("page.blocks.features.withProductScreenshot"),
            component: WithProductScreenShot,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Innovative Solutions", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Transform Your Business", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Our cutting-edge platform empowers businesses to streamline operations, enhance productivity, and achieve sustainable growth in today's competitive market.",
                    translations.value.text
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.src,
                    {
                        src: getPaceholderImageUrl("800/600", pageColors.value.brand),
                        alt: "Product screenshot",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "text",
                        },
                        iconSrc: {
                            label: translations.value.iconSrc,
                            type: "image",
                        },
                    },
                    [
                        {
                            title: "Seamless Integration",
                            description:
                                "Connect all your existing tools and systems effortlessly with our platform, eliminating data silos and creating a unified workflow experience.",
                            iconSrc: imageSmSrc,
                        },
                        {
                            title: "Advanced Security",
                            description:
                                "Protect your valuable business data with enterprise-grade security features including end-to-end encryption and multi-factor authentication.",
                            iconSrc: imageSmSrc,
                        },
                        {
                            title: "Automated Reporting",
                            description:
                                "Generate comprehensive insights and analytics automatically, helping you make data-driven decisions to optimize your business performance.",
                            iconSrc: imageSmSrc,
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                        iconSrc: imageSmSrc,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const withWideImagesBlock = computed<BlockDef>(() =>
        createBlock<WithWideImagesProps>({
            typeId: "WithWideImages",
            type: "features",
            name: t("page.blocks.features.withWideImages"),
            component: WithWideImages,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Features", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Everything you need to know", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "The Drawstring Canister comes with multiple strap and handle options to adapt throughout your day. Shoulder sling it, backpack it, or handy carry it.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        imageSrc: {
                            label: translations.value.src,
                            type: "image",
                        },
                        imageAlt: {
                            label: translations.value.alt,
                            type: "text",
                        },
                    },
                    [
                        {
                            title: "Adventure-ready",
                            description:
                                "The Drawstring Canister is water and tear resistant with durable canvas construction. This bag holds up to the demands of daily use while keeping your snacks secure.",
                            imageSrc: getPaceholderImageUrl("800/320", pageColors.value.brand),
                            imageAlt: "Adventure-ready",
                        },
                        {
                            title: "Minimal and clean",
                            description:
                                "Everything you need, nothing you don't. This bag has the simple, contemporary design that enables you to tell everyone you know about how essentialism is the only rational way to live life.",
                            imageSrc: getPaceholderImageUrl("800/320", pageColors.value.brand),
                            imageAlt: "Minimal and clean design",
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                        imageSrc: getPaceholderImageUrl("800/320", pageColors.value.brand),
                        imageAlt: "Feature image",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const withTieredImagesBlock = computed<BlockDef>(() =>
        createBlock<WithTieredImagesProps>({
            typeId: "WithTieredImages",
            type: "features",
            name: t("page.blocks.features.withTieredImages"),
            component: WithTieredImages,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Product Highlights", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Innovative technology", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Our premium smart device delivers exceptional performance with intuitive features that seamlessly integrate into your daily routine.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                    },
                    [
                        {
                            title: "Intuitive interface",
                            description:
                                "The streamlined control panel offers easy navigation with responsive touch controls and customizable settings.",
                        },
                        {
                            title: "Ergonomic design",
                            description:
                                "Carefully crafted for maximum comfort and efficiency during extended use sessions.",
                        },
                        {
                            title: "Smart connectivity",
                            description:
                                "Seamlessly connects to your devices via Bluetooth and WiFi, with voice command compatibility for hands-free operation.",
                        },
                        {
                            title: "Premium materials",
                            description:
                                "Constructed with high-grade components that ensure durability while maintaining a sophisticated aesthetic.",
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                    }
                )
            )
            .withField(
                "mainImage",
                descriptiveImageField(
                    translations.value.mainImage,
                    {
                        src: getPaceholderImageUrl("800/800", pageColors.value.brand),
                        alt: translations.value.alt,
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withArrayField(
                "secondaryImages",
                arrayField(
                    translations.value.secondaryImage,
                    "images",
                    {
                        imageSrc: { label: translations.value.src, type: "image" },
                        imageAlt: { label: translations.value.alt, type: "text" },
                    },
                    [
                        {
                            imageSrc: getPaceholderImageUrl("350/350", pageColors.value.brand),
                            imageAlt: "Interface close-up",
                        },
                        {
                            imageSrc: getPaceholderImageUrl("350/350", pageColors.value.brand),
                            imageAlt: "Product in use",
                        },
                    ],
                    {
                        imageSrc: getPaceholderImageUrl("350/350", pageColors.value.brand),
                        imageAlt: "New image",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const withTestimonialBlock = computed<BlockDef>(() =>
        createBlock<WithTestimonialProps>({
            typeId: "WithTestimonial",
            type: "features",
            name: t("page.blocks.features.withTestimonial"),
            component: WithTestimonial,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Testimonial", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "What Our Customers Say", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Discover how our platform has transformed the way businesses operate and helped them achieve their goals.",
                    translations.value.text
                )
            )
            .withField(
                "testimonial",
                htmlField(
                    translations.value.testimonial,
                    "The platform has completely revolutionized how we manage our projects. The intuitive interface and powerful features have made our workflow smoother and more efficient than ever before.",
                    translations.value.testimonial
                )
            )
            .withField(
                "primaryButton",
                buttonField(
                    translations.value.primaryButton,
                    {
                        label: "Get Started",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField("authorName", textField(translations.value.authorName, "John Doe"))
            .withField(
                "authorPosition",
                textField(translations.value.authorPosition, "Marketing Manager")
            )
            .withField(
                "authorIcon",
                imageField(
                    translations.value.authorIcon,
                    {
                        src: `${AVATAR_API_BASE_URL}/m/1`,
                    },
                    translations.value.src
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.src,
                    {
                        src: getPaceholderImageUrl("800/600", pageColors.value.brand),
                        alt: "Product dashboard screenshot",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const withSquareImagesBlock = computed<BlockDef>(() =>
        createBlock<WithSquareImagesProps>({
            typeId: "WithSquareImages",
            type: "features",
            name: t("page.blocks.features.withSquareImages"),
            component: WithSquareImages,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Features", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Productivity Enhancers", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Boost your workflow with these powerful features designed for efficiency and collaboration.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        imageSrc: {
                            label: translations.value.src,
                            type: "image",
                        },
                        imageAlt: {
                            label: translations.value.alt,
                            type: "text",
                        },
                    },
                    [
                        {
                            title: "Task Automation",
                            description: "Automate repetitive tasks to save valuable time.",
                            imageSrc: getPaceholderImageUrl("500/500", pageColors.value.brand),
                            imageAlt: "Data visualization dashboard",
                        },
                        {
                            title: "Real-time Sync",
                            description: "Keep your team aligned with seamless synchronization.",
                            imageSrc: getPaceholderImageUrl("500/500", pageColors.value.brand),
                            imageAlt: "Project management interface",
                        },
                        {
                            title: "Customizable Dashboards",
                            description: "Tailor your dashboard to focus on what matters most.",
                            imageSrc: getPaceholderImageUrl("500/500", pageColors.value.brand),
                            imageAlt: "Team communication tools",
                        },
                        {
                            title: "Advanced Reporting",
                            description: "Generate insights to make data-driven decisions.",
                            imageSrc: getPaceholderImageUrl("500/500", pageColors.value.brand),
                            imageAlt: "Team communication tools",
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                        imageSrc: getPaceholderImageUrl("500/500", pageColors.value.brand),
                        imageAlt: "Feature image",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const withLargeScreenshotBlock = computed<BlockDef>(() =>
        createBlock<WithLargeScreenshotProps>({
            typeId: "WithLargeScreenshot",
            type: "features",
            name: t("page.blocks.features.withLargeScreenshot"),
            component: WithLargeScreenshot,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Comprehensive Platform", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Powerful Tools", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Our platform provides everything you need to streamline operations, enhance productivity, and scale your business efficiently.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "text",
                        },
                        iconSrc: {
                            label: translations.value.iconSrc,
                            type: "image",
                        },
                    },
                    [
                        {
                            iconSrc: imageSmSrc,
                            title: "One-Click Deployment",
                            description:
                                "Deploy applications instantly with our streamlined process.",
                        },
                        {
                            iconSrc: imageSmSrc,
                            title: "Automated SSL Certificates",
                            description:
                                "Secure applications automatically with built-in certificate management.",
                        },
                        {
                            iconSrc: imageSmSrc,
                            title: "Enterprise-Grade Security",
                            description:
                                "Protect data with encryption and multi-factor authentication.",
                        },
                        {
                            iconSrc: imageSmSrc,
                            title: "Robust API Integration",
                            description:
                                "Connect seamlessly with third-party services through our API.",
                        },
                        {
                            iconSrc: imageSmSrc,
                            title: "Efficient Task Management",
                            description:
                                "Organize tasks with our intuitive queue system for better workflow.",
                        },
                        {
                            iconSrc: imageSmSrc,
                            title: "Automated Backups",
                            description:
                                "Keep data safe with scheduled backups and easy restoration.",
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                        iconSrc: imageSmSrc,
                    }
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.src,
                    {
                        src: getPaceholderImageUrl("1600/900", pageColors.value.brand),
                        alt: "Dashboard preview screenshot",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const withHeaderImagesAndDescriptionsBlock = computed<BlockDef>(() =>
        createBlock<WithHeaderImagesAndDescriptionsProps>({
            typeId: "WithHeaderImagesAndDescriptions",
            type: "features",
            name: t("page.blocks.features.withHeaderImagesAndDescriptions"),
            component: WithHeaderImagesAndDescriptions,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Features", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Our Key Features", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Innovative features designed to enhance your experience and deliver exceptional value.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        imageSrc: {
                            label: translations.value.src,
                            type: "image",
                        },
                        imageAlt: {
                            label: translations.value.alt,
                            type: "text",
                        },
                    },
                    [
                        {
                            description:
                                "Customizable interface that adapts to your workflow with intuitive controls and flexible settings.",
                            imageSrc: getPaceholderImageUrl("800/600", pageColors.value.brand),
                            imageAlt: "Data analytics dashboard",
                        },
                        {
                            description:
                                "Real-time collaboration tools for efficient teamwork, sharing, and project management.",
                            imageSrc: getPaceholderImageUrl("800/600", pageColors.value.brand),
                            imageAlt: "Project management interface",
                        },
                    ],
                    {
                        description: "Feature description",
                        imageSrc: getPaceholderImageUrl("800/600", pageColors.value.brand),
                        imageAlt: "Feature image",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const withAlternatingSectionsBlock = computed<BlockDef>(() =>
        createBlock<WithAlternatingSectionsProps>({
            typeId: "WithAlternatingSections",
            type: "features",
            name: t("page.blocks.features.withAlternatingSections"),
            component: WithAlternatingSections,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Features", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Protect your digital life", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Our laptop sleeves are designed with both style and functionality in mind. Each feature is carefully crafted to ensure your device stays protected while maintaining a sleek, professional appearance.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        imageSrc: {
                            label: translations.value.src,
                            type: "image",
                        },
                        imageAlt: {
                            label: translations.value.alt,
                            type: "text",
                        },
                    },
                    [
                        {
                            title: "Premium Materials",
                            description:
                                "Crafted from high-quality canvas with double-stitched construction and a soft felt interior that protects your device from scratches and impacts.",
                            imageSrc: getPaceholderImageUrl("800/320", pageColors.value.brand),
                            imageAlt: "Analytics dashboard",
                        },
                        {
                            title: "Smart Storage",
                            description:
                                "Thoughtfully designed with a convenient front pouch for your charger and accessories, keeping everything organized and easily accessible.",
                            imageSrc: getPaceholderImageUrl("800/320", pageColors.value.brand),
                            imageAlt: "Project management interface",
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                        imageSrc: getPaceholderImageUrl("800/320", pageColors.value.brand),
                        imageAlt: "Feature image",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const simpleThreeColumnWithSmallIconsBlock = computed<BlockDef>(() =>
        createBlock<SimpleThreeColumnWithSmallIconsProps>({
            typeId: "SimpleThreeColumnWithSmallIcons",
            type: "features",
            name: t("page.blocks.features.simpleThreeColumnWithSmallIcons"),
            component: SimpleThreeColumnWithSmallIcons,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Features", translations.value.text)
            )
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Everything you need to deploy your app",
                    "h2",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Our platform provides all the essential tools and features to help you deploy and manage your applications with ease, security, and reliability.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        linkHref: {
                            label: translations.value.linkHref,
                            type: "text",
                        },
                        linkLabel: {
                            label: translations.value.linkLabel,
                            type: "text",
                        },
                        iconSrc: {
                            label: translations.value.iconSrc,
                            type: "image",
                        },
                    },
                    [
                        {
                            iconSrc: imageSmSrc,
                            title: "Push to deploy",
                            description:
                                "Deploy your application with a simple git push. Our automated CI/CD pipeline handles the rest, ensuring your app is live within minutes.",
                            linkHref: "#",
                            linkLabel: "Learn More",
                        },
                        {
                            iconSrc: imageSmSrc,
                            title: "SSL certificates",
                            description:
                                "Secure your application with automatic SSL certificate generation and renewal. We handle the complexity of encryption so you can focus on development.",
                            linkHref: "#",
                            linkLabel: "Learn More",
                        },
                        {
                            iconSrc: imageSmSrc,
                            title: "Database backups",
                            description:
                                "Keep your data safe with scheduled automated backups. Easily restore to any point in time with our intuitive recovery system.",
                            linkHref: "#",
                            linkLabel: "Learn More",
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                        linkHref: "#",
                        linkLabel: "Learn More",
                        iconSrc: imageSmSrc,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const simpleThreeColumnWithLargeIconsBlock = computed<BlockDef>(() =>
        createBlock<SimpleThreeColumnWithLargeIconsProps>({
            typeId: "SimpleThreeColumnWithLargeIcons",
            type: "features",
            name: t("page.blocks.features.simpleThreeColumnWithLargeIcons"),
            component: SimpleThreeColumnWithLargeIcons,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Features", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Stay on top of customer support", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Enhance your customer service with our powerful support tools. Streamline communication, manage requests efficiently, and deliver exceptional customer experiences.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        linkHref: {
                            label: translations.value.linkHref,
                            type: "text",
                        },
                        linkLabel: {
                            label: translations.value.linkLabel,
                            type: "text",
                        },
                        iconSrc: {
                            label: translations.value.iconSrc,
                            type: "image",
                        },
                    },
                    [
                        {
                            iconSrc: imageMdSrc,
                            title: "Unlimited inboxes",
                            description:
                                "Create as many support inboxes as you need for different departments or products. Route messages automatically to the right team and keep everything organized in one place.",
                            linkHref: "#",
                            linkLabel: "Learn More",
                        },
                        {
                            iconSrc: imageMdSrc,
                            title: "Manage team members",
                            description:
                                "Assign tickets to specific team members, track performance metrics, and balance workloads effectively. Set permissions and roles to ensure the right people have access to the right tools.",
                            linkHref: "#",
                            linkLabel: "Learn More",
                        },
                        {
                            iconSrc: imageMdSrc,
                            title: "Spam report",
                            description:
                                "Automatically detect and filter spam messages with our advanced AI system. Generate detailed reports on spam patterns and protect your team from wasting time on irrelevant communications.",
                            linkHref: "#",
                            linkLabel: "Learn More",
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                        linkHref: "#",
                        linkLabel: "Learn More",
                        iconSrc: imageMdSrc,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const simpleBlock = computed<BlockDef>(() =>
        createBlock<SimpleProps>({
            typeId: "Simple",
            type: "features",
            name: t("page.blocks.features.simple"),
            component: Simple,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Features", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Everything you need", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Powerful features to help you manage, optimize, and grow your business effectively.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                    },
                    [
                        {
                            title: "Advanced Analytics",
                            description:
                                "Get deep insights into your business performance with intuitive dashboards. Visualize key metrics, track growth trends, and identify optimization opportunities.",
                        },
                        {
                            title: "Automation Tools",
                            description:
                                "Streamline workflows and save time with powerful automation capabilities. Create custom triggers and actions to handle repetitive tasks automatically. Reduce manual errors.",
                        },
                        {
                            title: "Secure Platform",
                            description:
                                "Enterprise-grade security to protect your data and ensure compliance. Our platform employs end-to-end encryption, regular security audits, and follows best practices.",
                        },
                        {
                            title: "Team Collaboration",
                            description:
                                "Work together seamlessly with built-in collaboration features. Share projects, assign tasks, track progress, and communicate efficiently. Our workspace eliminates silos between departments.",
                        },
                        {
                            title: "Custom Integration",
                            description:
                                "Connect with your favorite tools through our extensive API and integrations. Works with popular business applications, CRM systems, and marketing tools.",
                        },
                        {
                            title: "24/7 Support",
                            description:
                                "Get help when you need it with our dedicated support team. Our specialists are available around the clock to resolve issues and answer questions. Access our knowledge base and video tutorials.",
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const offsetWithFeatureListBlock = computed<BlockDef>(() =>
        createBlock<OffsetWithFeatureListProps>({
            typeId: "OffsetWithFeatureList",
            type: "features",
            name: t("page.blocks.features.offsetWithFeatureList"),
            component: OffsetWithFeatureList,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Features", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Everything you need to succeed", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Powerful features to help you manage, optimize, and grow your business with confidence.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                    },
                    [
                        {
                            title: "Advanced Analytics",
                            description:
                                "Get deep insights into your business performance with metrics.",
                        },
                        {
                            title: "Team Collaboration",
                            description:
                                "Work seamlessly with your team in real-time across all projects.",
                        },
                        {
                            title: "Automated Workflows",
                            description:
                                "Save time and reduce errors with intelligent automation tools.",
                        },
                        {
                            title: "Custom Reports",
                            description:
                                "Create detailed reports to track and visualize key business metrics.",
                        },
                        {
                            title: "Data Security",
                            description:
                                "Enterprise-grade security to protect sensitive information.",
                        },
                        {
                            title: "Mobile Access",
                            description: "Access your dashboard from anywhere on any device.",
                        },
                        {
                            title: "Integration Ecosystem",
                            description:
                                "Connect with your favorite tools through our extensive API.",
                        },
                        {
                            title: "Customer Support",
                            description:
                                "Get help when you need it with our 24/7 dedicated support team.",
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const offset2x2GridBlock = computed<BlockDef>(() =>
        createBlock<Offset2x2GridProps>({
            typeId: "Offset2x2Grid",
            type: "features",
            name: t("page.blocks.features.offset2x2Grid"),
            component: Offset2x2Grid,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Features", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Build better products", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Empower your team with our comprehensive suite of tools and features designed to streamline your workflow and enhance productivity.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: "Title",
                            type: "text",
                        },
                        description: {
                            label: "Description",
                            type: "html",
                        },
                        iconSrc: {
                            label: "Icon",
                            type: "image",
                        },
                    },
                    [
                        {
                            title: "Automated Workflows",
                            description:
                                "Build custom automation workflows that save time and reduce errors in your development process.",
                            iconSrc: imageMdSrc,
                        },
                        {
                            title: "Real-time Collaboration",
                            description:
                                "Work together seamlessly with your team in real-time, no matter where they are located.",
                            iconSrc: imageMdSrc,
                        },
                        {
                            title: "Advanced Analytics",
                            description:
                                "Get deep insights into your project performance with detailed analytics and reporting.",
                            iconSrc: imageMdSrc,
                        },
                        {
                            title: "Security First",
                            description:
                                "Enterprise-grade security features to keep your data and team protected at all times.",
                            iconSrc: imageMdSrc,
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                        iconSrc: imageMdSrc,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const containedInPanelBlock = computed<BlockDef>(() =>
        createBlock<ContainedInPanelProps>({
            typeId: "ContainedInPanel",
            type: "features",
            name: t("page.blocks.features.containedInPanel"),
            component: ContainedInPanel,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Features", translations.value.text)
            )
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Everything you need to manage your business",
                    "h2",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Our comprehensive suite of tools helps you streamline operations, boost productivity, and make data-driven decisions with confidence.",
                    translations.value.text
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.src,
                    {
                        src: getPaceholderImageUrl("800/600", pageColors.value.brand),
                        alt: "Feature image",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        iconSrc: {
                            label: translations.value.iconSrc,
                            type: "image",
                        },
                    },
                    [
                        {
                            title: "Advanced Analytics",
                            description:
                                "Get deep insights into your business performance with our powerful analytics tools.",
                            iconSrc: imageSmSrc,
                        },
                        {
                            title: "Team Collaboration",
                            description:
                                "Work seamlessly with your team using our integrated collaboration features.",
                            iconSrc: imageSmSrc,
                        },
                        {
                            title: "Automated Workflows",
                            description:
                                "Save time and reduce errors with our intelligent automation capabilities.",
                            iconSrc: imageSmSrc,
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                        iconSrc: getPaceholderImageUrl("48/48", pageColors.value.brand),
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
                colorDefaults.value.cardBackground,
                colorDefaults.value.cardText,
            ])
            .build()
    );

    const withProductScreenshotOnLeftBlock = computed<BlockDef>(() =>
        createBlock<WithProductScreenshotOnLeftProps>({
            typeId: "WithProductScreenshotOnLeft",
            type: "features",
            name: t("page.blocks.features.withProductScreenshotOnLeft"),
            component: WithProductScreenshotOnLeft,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Deploy faster", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "A better workflow", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Streamline your development process with our powerful tools designed to enhance productivity and reduce deployment time. Focus on what matters most - building great products.",
                    translations.value.text
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.src,
                    {
                        src: getPaceholderImageUrl("800/600", pageColors.value.brand),
                        alt: "Product screenshot",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "text",
                        },
                        iconSrc: {
                            label: translations.value.iconSrc,
                            type: "image",
                        },
                    },
                    [
                        {
                            iconSrc: imageSmSrc,
                            title: "Push to deploy.",
                            description:
                                "Deploy your code with a simple git push. Our automated CI/CD pipeline handles testing, building, and deployment so you can focus on writing great code.",
                        },
                        {
                            iconSrc: imageSmSrc,
                            title: "SSL certificates.",
                            description:
                                "Automatic SSL certificate generation and renewal keeps your applications secure. We handle all the complexity of encryption and security protocols for you.",
                        },
                        {
                            iconSrc: imageSmSrc,
                            title: "Database backups.",
                            description:
                                "Regular automated database backups ensure your data is always protected. Easily restore to any point in time with our intuitive recovery interface.",
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                        iconSrc: imageSmSrc,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const withSplitImageBlock = computed<BlockDef>(() =>
        createBlock<WithSplitImageProps>({
            typeId: "WithSplitImage",
            type: "features",
            name: t("page.blocks.features.withSplitImage"),
            component: WithSplitImage,
        })
            .withField(
                "tag",
                textField(translations.value.tag, "Features", translations.value.text)
            )
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Everything you need to know about our product",
                    "h2",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Our product is designed with your needs in mind. We've carefully crafted each feature to ensure you get the most out of your experience.",
                    translations.value.text
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.src,
                    {
                        src: getPaceholderImageUrl("1080/1080", pageColors.value.brand),
                        alt: "Product features showcase",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                    },
                    [
                        {
                            title: "Easy to Use",
                            description:
                                "Intuitive interface that makes it simple to get started and accomplish your goals quickly.",
                        },
                        {
                            title: "Customizable",
                            description:
                                "Adapt the product to your specific needs with extensive customization options to match your business processes.",
                        },
                        {
                            title: "Reliable",
                            description:
                                "Built with stability in mind, ensuring your work is always safe and accessible with enterprise-grade security.",
                        },
                        {
                            title: "Scalable",
                            description:
                                "Grows with your needs, from small projects to enterprise-level operations while maintaining consistent performance.",
                        },
                    ],
                    {
                        title: "New Feature",
                        description: "Feature description",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    return {
        blocks: computed(() => [
            withProductScreenshotBlock.value,
            withLargeScreenshotBlock.value,
            simpleThreeColumnWithSmallIconsBlock.value,
            withProductScreenshotOnLeftBlock.value,
            simpleThreeColumnWithLargeIconsBlock.value,
            containedInPanelBlock.value,
            withTestimonialBlock.value,
            offset2x2GridBlock.value,
            offsetWithFeatureListBlock.value,
            simpleBlock.value,
            centered2x2GridBlock.value,
            withHeaderImagesAndDescriptionsBlock.value,
            withWideImagesBlock.value,
            withSplitImageBlock.value,
            withAlternatingSectionsBlock.value,
            withSquareImagesBlock.value,
            withTieredImagesBlock.value,
        ]),
    };
}
